// Names and tempo, read off what the producer already typed: folder and file
// names. Nothing here listens to the audio.
//
//   title   the dropped folder's (or zip's) name, tidied
//   tracks  each file's name, minus the prefix every file shares — DAWs name
//           exports "<project>_<track>", and "Kick" reads better than
//           "Summer Beat 3_Kick" seven times over
//   bpm     an explicit tempo ("120bpm", "120 BPM", "bpm120") anywhere in the
//           folder or file names; failing that, a bare number between
//           separators ("_120_") that sits in a sane tempo range. Null when
//           nothing says — the form asks.

/** The tempo range the server accepts for a seed. */
const MIN_BPM = 20;
const MAX_BPM = 999;
/** A bare number is only a tempo if it looks like one. */
const BARE_MIN = 60;
const BARE_MAX = 200;

const EXPLICIT = [
  /(\d{2,3}(?:\.\d+)?)\s*[-_ ]?\s*bpm(?![a-z])/i,
  /(?<![a-z])bpm\s*[-_: ]?\s*(\d{2,3}(?:\.\d+)?)/i,
];
const BARE = /(?:^|[\s_\-.()[\]])(\d{2,3})(?=$|[\s_\-.()[\]])/g;

/** The tempo one name states, or null. */
export function bpmFromName(name: string): number | null {
  for (const re of EXPLICIT) {
    const m = re.exec(name);
    if (m) {
      const bpm = Number(m[1]);
      if (bpm >= MIN_BPM && bpm <= MAX_BPM) return bpm;
    }
  }
  return null;
}

function bareBpm(name: string): number | null {
  for (const m of name.matchAll(BARE)) {
    const n = Number(m[1]);
    if (n >= BARE_MIN && n <= BARE_MAX) return n;
  }
  return null;
}

/**
 * The tempo the names agree on. The folder's name wins, then the most common
 * explicit tempo across the files, then a bare number in the folder's name.
 */
export function bpmFromNames(rootName: string | null, paths: string[]): number | null {
  if (rootName) {
    const fromRoot = bpmFromName(rootName);
    if (fromRoot !== null) return fromRoot;
  }
  const votes = new Map<number, number>();
  for (const p of paths) {
    const bpm = bpmFromName(p);
    if (bpm !== null) votes.set(bpm, (votes.get(bpm) ?? 0) + 1);
  }
  const best = [...votes].sort((a, b) => b[1] - a[1])[0];
  if (best) return best[0];
  return rootName ? bareBpm(rootName) : null;
}

/** A folder or zip name as a seed title: separators to spaces, tempo and "stems" dropped. */
export function titleFromName(name: string | null): string {
  if (!name) return "";
  return name
    .replace(/(\d{2,3}(?:\.\d+)?)\s*[-_ ]?\s*bpm(?![a-z])/gi, " ")
    .replace(/(?<![a-z])bpm\s*[-_: ]?\s*\d{2,3}(?:\.\d+)?/gi, " ")
    // Underscores are word characters to \b: make them spaces first.
    .replace(/_+/g, " ")
    .replace(/\b(stems?|multitracks?|exports?|bounces?)\b/gi, " ")
    .replace(/\s*-\s*$/g, "")
    .replace(/^\s*-\s*/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * Track names for a set of file names: the shared prefix, when it ends at a
 * separator and leaves every name something, is dropped.
 */
export function trackNames(names: string[]): string[] {
  if (names.length < 2) return names.map(tidy);
  const strip = sharedPrefix(names).length;
  const stripped = names.map((n) => tidy(n.slice(strip)));
  return stripped.every((n) => n.length > 0) ? stripped : names.map(tidy);
}

function tidy(name: string): string {
  return name.replace(/_+/g, " ").replace(/\s{2,}/g, " ").trim() || name;
}

/**
 * The prefix every name shares, cut back to its last separator so "Bass" and
 * "Bassline" don't lose "Bass". Empty for fewer than two names.
 */
export function sharedPrefix(names: string[]): string {
  if (names.length < 2) return "";
  let prefix = names[0]!;
  for (const n of names.slice(1)) {
    let i = 0;
    while (i < prefix.length && i < n.length && prefix[i] === n[i]) i++;
    prefix = prefix.slice(0, i);
  }
  const cut = Math.max(prefix.lastIndexOf("_"), prefix.lastIndexOf("-"), prefix.lastIndexOf(" "));
  return cut >= 0 ? prefix.slice(0, cut + 1) : "";
}

/** A title for loose files with no folder: what their names share ("Night Drive_Kick", … → "Night Drive"),
 *  or a lone file's own name — a bounce or a phone's voice memo is the song. */
export function titleFromFiles(names: string[]): string {
  if (names.length === 1) return titleFromName(names[0]!);
  return titleFromName(sharedPrefix(names).replace(/[-_ ]+$/, ""));
}
